import {
  CustomButton,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomModal,
  CustomSelect,
  errorDialog,
  Option,
  successDialog,
  Text,
  useText,
} from '@src/components';
import iconTick from '@src/assets/icons/tick-circle.svg';
import modalClose from '@src/assets/icons/icon-close.svg';
import React, { useCallback, useEffect, useState } from 'react';
import '@src/styles/myOrders/paymentModal.scss';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'antd';
import { formMailRegex } from '@src/utils/formRule';
import { getMailList, paymentLinksAdd } from '@src/store/slice/orderSlice';
import PropTypes from 'prop-types';
import { VouIdCode } from '@src/store/enums';

const PaymentModal = ({
  modalVisible,
  handleModalVisible,
  fleetLoadBalanceId = null,
  paymentPage = false,
  sendOrderEmail = null,
  isCompliments = false,
  isMarket = false,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { selectedOrder, mailList } = useSelector((state) => state?.order);
  const [isSelectValid, setIsSelectValid] = useState(true);
  const [isMailInputValid, setIsMailInputValid] = useState(true);
  let vouId;
  if (isMarket) {
    vouId = VouIdCode.Market;
  } else if (isCompliments) {
    vouId = VouIdCode.Compliments;
  } else {
    vouId = VouIdCode.Restaurant;
  }
  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');

  useEffect(() => {
    if (modalVisible && mailList.length === 0) {
      (async () => {
        await dispatch(getMailList(selectedCustomer?.customerId));
      })();
    }
  }, [dispatch, modalVisible, selectedCustomer, mailList]);

  const handleClose = useCallback(() => {
    form.resetFields();
    handleModalVisible(false);
    setIsSelectValid(true);
    setIsMailInputValid(true);
  }, [handleModalVisible, form]);

  const mailListChange = useCallback(
    (mail) => {
      if (mail && mail !== 0) {
        form.resetFields(['emailAddress']);
        setIsMailInputValid(false);
      } else {
        setIsMailInputValid(true);
      }
    },
    [form],
  );

  const mailOnChange = useCallback(
    (e) => {
      const { value } = e.target;
      if (value) {
        form.resetFields(['emailAddressList']);
        setIsSelectValid(false);
      } else {
        setIsSelectValid(true);
      }
    },
    [form],
  );

  const onFinish = useCallback(
    async (values) => {
      if (values?.emailAddress || values?.emailAddressList) {
        const body = {
          emailAddress: values?.emailAddress || values?.emailAddressList,
          totalPrice: selectedOrder?.paymentTotalPrice,
          orderId: fleetLoadBalanceId ?? selectedOrder?.id,
          orderDate: fleetLoadBalanceId ?? selectedOrder?.orderDate,
          vouId: vouId,
        };

        if (fleetLoadBalanceId) {
          sendOrderEmail(body);
        } else {
          const action = await dispatch(paymentLinksAdd(body));
          if (paymentLinksAdd.fulfilled.match(action)) {
            setIsMailInputValid(false);
            if (isCompliments || isMarket) {
              const warningHtmlContent = (
                <div className="is-compliments-payment">
                  <CustomImage src={iconTick} />
                  <Text t="paymentLinkSuccessfullySent" />
                </div>
              );
              successDialog({
                title: <Text t="success" />,
                htmlContent: warningHtmlContent,
                onOk: async () => {
                  await handleClose();
                },
              });
            } else {
              successDialog({
                title: <Text t="success" />,
                message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
                onOk: async () => {
                  await handleClose();
                },
              });
            }
          }
        }
      } else {
        errorDialog({
          title: <Text t="error" />,
          message: <Text t="paymentModalErrorMessage" />,
        });
      }
    },
    [fleetLoadBalanceId, isCompliments, isMarket, selectedOrder, dispatch, handleClose],
  );

  return (
    <CustomModal
      className={
        isCompliments || isMarket ? 'payment-modal payment-modal-compliments' : 'payment-modal'
      }
      maskClosable={false}
      footer={false}
      title={useText('sendPaymentLink')}
      visible={modalVisible}
      onCancel={handleClose}
      closeIcon={
        <div className="payment-modal-close-icon" data-testid="close-icon">
          <CustomImage src={modalClose} />
        </div>
      }
    >
      <div className="payment-container">
        {isCompliments || isMarket ? (
          <span className="modal-head">
            <Text t="sendPaymentLink" />
          </span>
        ) : null}
        <CustomForm
          name="paymentLinkForm"
          className="payment-link-form"
          form={form}
          initialValues={{}}
          onFinish={onFinish}
          autoComplete="off"
          layout={'horizontal'}
        >
          <p style={{ margin: isCompliments || isMarket ? 24 : 0 }}>
            <Text t="toSendThePaymentLinkYouCanSelect" />
          </p>
          <div className={isCompliments || isMarket ? 'is-compliments' : ''}>
            <CustomFormItem
              label={
                <Text t={fleetLoadBalanceId ? 'Yetkili E-Posta Listesi:' : 'authorizedMailList'} />
              }
              name="emailAddressList"
              rules={[{ required: isSelectValid, message: <Text t="requiredField" /> }]}
            >
              <CustomSelect
                id="ticketRestaurant-myOrders-paymentModal-emailAddressList-select"
                data-testid="email-select"
                placeholder={useText('choose')}
                height={36}
                disabled={!isSelectValid}
                onChange={mailListChange}
              >
                <Option
                  id="ticketRestaurant-myOrders-paymentModal-emailAddressList-choose-option"
                  value={0}
                >
                  <Text t="choose" />
                </Option>
                {mailList.map((item) => (
                  <Option
                    id={`ticketRestaurant-myOrders-paymentModal-emailAddressList-${item?.email}-option`}
                    data-testid={`option-${item.id}`}
                    key={item.id}
                    value={item.email}
                  >
                    {item.email}
                  </Option>
                ))}
              </CustomSelect>
            </CustomFormItem>
            {isCompliments || isMarket ? <span><Text t="or" /></span> : null}
            {!paymentPage && (
              <CustomFormItem
                label={<Text t="emailAdress" />}
                name="emailAddress"
                rules={[
                  { required: isMailInputValid, message: <Text t="pleaseEnterEmail" /> },
                  { validator: formMailRegex, message: <Text t="enterValidEmail" /> },
                ]}
              >
                <CustomInput
                  id="ticketRestaurant-myOrders-paymentModal-emailAddress-input"
                  data-testid="email-input"
                  disabled={!isMailInputValid}
                  onChange={mailOnChange}
                  height={36}
                />
              </CustomFormItem>
            )}
          </div>
          <CustomFormItem
            className={`${fleetLoadBalanceId || isCompliments || isMarket
              ? 'footer-form-item-fleet'
              : 'footer-form-item'
              }`}
          >
            <CustomButton
              id="ticketRestaurant-myOrders-paymentModal-submit-btn"
              data-testid="submit"
              className={`submit-btn ${fleetLoadBalanceId || isCompliments || isMarket ? 'submit-btn-fleet' : ''
                }`}
              type="primary"
              htmlType="submit"
            >
              <span className="submit">
                <Text t={isCompliments || isMarket ? 'LİNK GÖNDER' : 'sendLink'} />
              </span>
            </CustomButton>
          </CustomFormItem>
        </CustomForm>
      </div>
    </CustomModal>
  );
};

PaymentModal.propTypes = {
  modalVisible: PropTypes.bool,
  handleModalVisible: PropTypes.func,
  fleetLoadBalanceId: PropTypes.any,
  paymentPage: PropTypes.bool,
  sendOrderEmail: PropTypes.func,
  isCompliments: PropTypes.bool,
  isMarket: PropTypes.bool,
};

export default PaymentModal;
